import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Icon, type IconName } from '../ui/Icon'
import { INK_COLOURS, NOTE_COLOURS, SHAPES, type El, type Op, type Props, type Route, type ShapeKind } from './model'
import { layoutText, shapePath } from './ElementView'
import { AskGlyph } from './AskBox'

/**
 * The whiteboard's controls, laid out the way Miro lays them (studied on miro.com, 2026-10-29): the creation tools
 * in a column at the left edge, a bar of what can be changed floating above whatever is selected, zoom at the bottom
 * right. On a phone the tools are a row along the bottom and the bar stands at the top.
 */
export type Tool = 'select' | 'hand' | 'sticky' | 'text' | 'shape' | 'line' | 'pen' | 'frame' | 'ask'

function ShapeGlyph({ kind, size = 20 }: { kind: ShapeKind; size?: number }) {
  const w = size
  const h = kind === 'ellipse' || kind === 'triangle' ? size : size * 0.7
  return (
    <svg width={size + 2} height={size + 2} viewBox={`-1 -1 ${w + 2} ${size + 2}`} aria-hidden="true">
      <path d={shapePath(kind, w, h)} transform={`translate(0,${(size - h) / 2})`} fill="none" stroke="currentColor" strokeWidth={1.6} />
    </svg>
  )
}

function ToolButton({
  icon,
  glyph,
  label,
  keyHint,
  on,
  onClick,
  disabled,
}: {
  icon?: IconName
  glyph?: ReactNode
  label: string
  keyHint?: string
  on?: boolean
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className={`bd-tool${on ? ' on' : ''}`}
      title={keyHint ? `${label} (${keyHint})` : label}
      aria-label={label}
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
    >
      {glyph ?? (icon ? <Icon name={icon} size={20} /> : null)}
    </button>
  )
}

/** Closes when a press lands outside it. */
function Popover({ onClose, className, children }: { onClose: () => void; className: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const down = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    // Effects run after the press that opened it has finished, so that press is never taken for one outside.
    window.addEventListener('pointerdown', down, true)
    return () => window.removeEventListener('pointerdown', down, true)
  }, [onClose])
  return (
    <div ref={ref} className={`bd-pop ${className}`} role="dialog">
      {children}
    </div>
  )
}

export function Toolbar({
  tool,
  setTool,
  shape,
  setShape,
  note,
  setNote,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onImage,
  canAsk,
}: {
  tool: Tool
  setTool: (t: Tool) => void
  shape: ShapeKind
  setShape: (k: ShapeKind) => void
  note: string
  setNote: (hex: string) => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  /** Pick pictures to put on the board (a drop or a paste puts them there too). */
  onImage: () => void
  /** Questions about an area can be asked here (BoardCanvas `ask`). */
  canAsk?: boolean
}) {
  const [open, setOpen] = useState<'shape' | 'note' | null>(null)
  return (
    <div className="bd-toolbar" role="toolbar" aria-label="Whiteboard tools">
      <ToolButton icon="arrow_selector_tool" label="Select" keyHint="V" on={tool === 'select'} onClick={() => setTool('select')} />
      <ToolButton icon="pan_tool" label="Move the board" keyHint="H" on={tool === 'hand'} onClick={() => setTool('hand')} />
      <div className="bd-tool-wrap">
        <ToolButton
          glyph={<span className="bd-note-glyph" style={{ background: note }} />}
          label="Sticky note"
          keyHint="N"
          on={tool === 'sticky'}
          onClick={() => {
            setTool('sticky')
            setOpen(open === 'note' ? null : 'note')
          }}
        />
        {open === 'note' && (
          <Popover className="bd-pop-tools" onClose={() => setOpen(null)}>
            <div className="bd-swatches">
              {NOTE_COLOURS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  className={`bd-swatch${c.hex === note ? ' on' : ''}`}
                  style={{ background: c.hex }}
                  title={c.name}
                  aria-label={`${c.name} note`}
                  onClick={() => {
                    setNote(c.hex)
                    setTool('sticky')
                    setOpen(null)
                  }}
                />
              ))}
            </div>
          </Popover>
        )}
      </div>
      <ToolButton icon="title" label="Text" keyHint="T" on={tool === 'text'} onClick={() => setTool('text')} />
      <div className="bd-tool-wrap">
        <ToolButton
          glyph={<ShapeGlyph kind={shape} />}
          label="Shapes"
          keyHint="S"
          on={tool === 'shape'}
          onClick={() => {
            setTool('shape')
            setOpen(open === 'shape' ? null : 'shape')
          }}
        />
        {open === 'shape' && (
          <Popover className="bd-pop-tools" onClose={() => setOpen(null)}>
            <div className="bd-shape-grid">
              {SHAPES.map((s) => (
                <button
                  key={s.kind}
                  type="button"
                  className={`bd-shape-btn${s.kind === shape ? ' on' : ''}`}
                  title={s.label}
                  aria-label={s.label}
                  onClick={() => {
                    setShape(s.kind)
                    setTool('shape')
                    setOpen(null)
                  }}
                >
                  <ShapeGlyph kind={s.kind} size={22} />
                </button>
              ))}
            </div>
          </Popover>
        )}
      </div>
      <ToolButton icon="trending_flat" label="Connection line" keyHint="L" on={tool === 'line'} onClick={() => setTool('line')} />
      <ToolButton icon="draw" label="Pen" keyHint="P" on={tool === 'pen'} onClick={() => setTool('pen')} />
      <ToolButton icon="crop_free" label="Frame" keyHint="F" on={tool === 'frame'} onClick={() => setTool('frame')} />
      <ToolButton icon="image" label="Image" onClick={onImage} />
      <span className="bd-tool-sep" />
      {/* Box an area and ask about it (board/area.ts; owner, 2026-10-31). */}
      {canAsk && (
        <>
          <ToolButton glyph={<AskGlyph />} label="Ask about an area" keyHint="A" on={tool === 'ask'} onClick={() => setTool(tool === 'ask' ? 'select' : 'ask')} />
          <span className="bd-tool-sep" />
        </>
      )}
      <ToolButton icon="undo" label="Undo" keyHint="⌘Z" onClick={onUndo} disabled={!canUndo} />
      <ToolButton icon="redo" label="Redo" keyHint="⇧⌘Z" onClick={onRedo} disabled={!canRedo} />
    </div>
  )
}

/**
 * THE TOOLS OF SOMEONE WHO MAY ONLY LOOK AT THE BOARD (its lock, board/EditLock) — owner, 2026-11-03: 被鎖的人也要能提問,
 * 做一個只有訊息按鈕的工具列. Asking about an area is a message to the project chat, not a change to the board.
 */
export function AskOnlyToolbar({ tool, setTool }: { tool: Tool; setTool: (t: Tool) => void }) {
  return (
    <div className="bd-toolbar bd-toolbar-ask" role="toolbar" aria-label="Whiteboard tools">
      <ToolButton glyph={<AskGlyph />} label="Ask about an area" keyHint="A" on={tool === 'ask'} onClick={() => setTool(tool === 'ask' ? 'select' : 'ask')} />
    </div>
  )
}

export function ZoomBar({ scale, onZoom, onFit, onReset }: { scale: number; onZoom: (f: number) => void; onFit: () => void; onReset: () => void }) {
  return (
    <div className="bd-zoombar" role="group" aria-label="Zoom">
      <button type="button" className="bd-zbtn" title="Fit everything (⇧1)" aria-label="Fit everything" onClick={onFit}>
        <Icon name="fit_screen" size={18} />
      </button>
      <button type="button" className="bd-zbtn" title="Zoom out" aria-label="Zoom out" onClick={() => onZoom(1 / 1.25)}>
        <Icon name="remove" size={18} />
      </button>
      <button type="button" className="bd-zpct" title="Back to 100 %" onClick={onReset}>
        {Math.round(scale * 100)}%
      </button>
      <button type="button" className="bd-zbtn" title="Zoom in" aria-label="Zoom in" onClick={() => onZoom(1.25)}>
        <Icon name="add" size={18} />
      </button>
    </div>
  )
}

function Swatches({ colours, value, onPick, none }: { colours: Array<{ name: string; hex: string }>; value?: string | undefined; onPick: (hex: string) => void; none?: boolean }) {
  return (
    <div className="bd-swatches">
      {none && (
        <button type="button" className={`bd-swatch bd-swatch-none${value === 'none' ? ' on' : ''}`} title="None" aria-label="None" onClick={() => onPick('none')} />
      )}
      {colours.map((c) => (
        <button
          key={c.hex}
          type="button"
          className={`bd-swatch${c.hex === value ? ' on' : ''}`}
          style={{ background: c.hex }}
          title={c.name}
          aria-label={c.name}
          onClick={() => onPick(c.hex)}
        />
      ))}
    </div>
  )
}

type Menu = 'fill' | 'stroke' | 'color' | 'shape' | null

/**
 * What can be changed about the selection — only what applies to ALL of it. Every change is one write and one undo
 * step (`onChange` builds the ops).
 */
export function ContextBar({
  sel,
  onChange,
  onDelete,
  onDuplicate,
  onFront,
  onBack,
  style,
}: {
  sel: El[]
  onChange: (ops: Op[]) => void
  onDelete: () => void
  onDuplicate: () => void
  onFront: () => void
  onBack: () => void
  style: React.CSSProperties
}) {
  const [menu, setMenu] = useState<Menu>(null)
  const kinds = new Set(sel.map((e) => e.t))
  const one = sel.length === 1 ? sel[0]! : null
  const only = (k: El['t']) => kinds.size === 1 && kinds.has(k)
  const setAll = (props: Props, filter: (e: El) => boolean = () => true) =>
    onChange(sel.filter(filter).map((e) => ({ id: e.id, set: props })))
  const texty = sel.every((e) => e.t === 'sticky' || e.t === 'shape' || e.t === 'text' || e.t === 'line')
  const fsOf = (e: El) => (e.t === 'line' ? e.fs ?? 14 : layoutText(e).fs)
  const step = (dir: 1 | -1) =>
    onChange(
      sel.map((e) => {
        const cur = fsOf(e)
        const next = Math.max(8, Math.min(120, Math.round(dir > 0 ? cur * 1.2 + 1 : cur / 1.2 - 1)))
        return { id: e.id, set: { fs: next } }
      }),
    )
  const first = sel[0]
  return (
    <div className="bd-context" style={style} role="toolbar" aria-label="Change the selection" onPointerDown={(e) => e.stopPropagation()}>
      {only('sticky') && (
        <div className="bd-ctx-group">
          <Swatches colours={NOTE_COLOURS} value={first?.fill} onPick={(hex) => setAll({ fill: hex })} />
        </div>
      )}
      {only('shape') && one && (
        <div className="bd-ctx-wrap">
          <button type="button" className="bd-ctx-btn" title="Shape" aria-label="Change the shape" onClick={() => setMenu(menu === 'shape' ? null : 'shape')}>
            <ShapeGlyph kind={one.shape ?? 'rect'} size={18} />
          </button>
          {menu === 'shape' && (
            <Popover className="bd-pop-ctx" onClose={() => setMenu(null)}>
              <div className="bd-shape-grid">
                {SHAPES.map((s) => (
                  <button
                    key={s.kind}
                    type="button"
                    className={`bd-shape-btn${s.kind === one.shape ? ' on' : ''}`}
                    title={s.label}
                    aria-label={s.label}
                    onClick={() => {
                      setAll({ shape: s.kind })
                      setMenu(null)
                    }}
                  >
                    <ShapeGlyph kind={s.kind} size={22} />
                  </button>
                ))}
              </div>
            </Popover>
          )}
        </div>
      )}
      {(only('shape') || only('text') || only('frame')) && (
        <div className="bd-ctx-wrap">
          <button type="button" className="bd-ctx-btn" title="Fill colour" aria-label="Fill colour" onClick={() => setMenu(menu === 'fill' ? null : 'fill')}>
            <span className="bd-dot" style={{ background: first?.fill && first.fill !== 'none' ? first.fill : '#ffffff' }} />
          </button>
          {menu === 'fill' && (
            <Popover className="bd-pop-ctx" onClose={() => setMenu(null)}>
              <Swatches colours={NOTE_COLOURS} value={first?.fill} none onPick={(hex) => setAll({ fill: hex })} />
            </Popover>
          )}
        </div>
      )}
      {(only('shape') || only('line') || only('pen')) && (
        <div className="bd-ctx-wrap">
          <button type="button" className="bd-ctx-btn" title={only('shape') ? 'Border colour' : 'Line colour'} aria-label="Line colour" onClick={() => setMenu(menu === 'stroke' ? null : 'stroke')}>
            <span className="bd-ring" style={{ borderColor: first?.stroke && first.stroke !== 'none' ? first.stroke : '#1e1e1e' }} />
          </button>
          {menu === 'stroke' && (
            <Popover className="bd-pop-ctx" onClose={() => setMenu(null)}>
              <Swatches colours={INK_COLOURS} value={first?.stroke} none={only('shape')} onPick={(hex) => setAll({ stroke: hex })} />
              <div className="bd-ctx-row">
                {[1, 2, 4, 6].map((w) => (
                  <button key={w} type="button" className={`bd-width${(first?.sw ?? (only('pen') ? 3 : 2)) === w ? ' on' : ''}`} aria-label={`Width ${w}`} onClick={() => setAll({ sw: w })}>
                    <span style={{ height: w }} />
                  </button>
                ))}
              </div>
            </Popover>
          )}
        </div>
      )}
      {(only('shape') || only('line')) && (
        <button type="button" className={`bd-ctx-btn${first?.dash ? ' on' : ''}`} title="Dashed" aria-label="Dashed" aria-pressed={!!first?.dash} onClick={() => setAll({ dash: !first?.dash })}>
          <Icon name="line_style" size={18} />
        </button>
      )}
      {only('line') && one && (
        <div className="bd-ctx-group">
          {(['elbow', 'straight', 'curve'] as Route[]).map((r) => (
            <button
              key={r}
              type="button"
              className={`bd-ctx-btn bd-ctx-text${(one.route ?? 'elbow') === r ? ' on' : ''}`}
              title={r === 'elbow' ? 'Elbowed' : r === 'straight' ? 'Straight' : 'Curved'}
              onClick={() => setAll({ route: r })}
            >
              {r === 'elbow' ? '┐' : r === 'straight' ? '╱' : '∿'}
            </button>
          ))}
          <button
            type="button"
            className={`bd-ctx-btn bd-ctx-text${one.tail === 'arrow' ? ' on' : ''}`}
            title="Arrow at the start"
            aria-pressed={one.tail === 'arrow'}
            onClick={() => setAll({ tail: one.tail === 'arrow' ? 'none' : 'arrow' })}
          >
            ←
          </button>
          <button
            type="button"
            className={`bd-ctx-btn bd-ctx-text${(one.head ?? 'arrow') === 'arrow' ? ' on' : ''}`}
            title="Arrow at the end"
            aria-pressed={(one.head ?? 'arrow') === 'arrow'}
            onClick={() => setAll({ head: (one.head ?? 'arrow') === 'arrow' ? 'none' : 'arrow' })}
          >
            →
          </button>
        </div>
      )}
      {texty && (
        <div className="bd-ctx-group">
          <button type="button" className="bd-ctx-btn" title="Smaller text" aria-label="Smaller text" onClick={() => step(-1)}>
            <Icon name="text_decrease" size={18} />
          </button>
          <button type="button" className="bd-ctx-btn" title="Larger text" aria-label="Larger text" onClick={() => step(1)}>
            <Icon name="text_increase" size={18} />
          </button>
          <button type="button" className={`bd-ctx-btn${first?.bold ? ' on' : ''}`} title="Bold" aria-label="Bold" aria-pressed={!!first?.bold} onClick={() => setAll({ bold: !first?.bold })}>
            <Icon name="format_bold" size={18} />
          </button>
          {!only('line') && (
            <button
              type="button"
              className="bd-ctx-btn"
              title="Text alignment"
              aria-label="Text alignment"
              onClick={() => {
                const cur = first?.align ?? (first?.t === 'text' ? 'left' : 'center')
                setAll({ align: cur === 'left' ? 'center' : cur === 'center' ? 'right' : 'left' })
              }}
            >
              <Icon
                name={
                  (first?.align ?? (first?.t === 'text' ? 'left' : 'center')) === 'left'
                    ? 'format_align_left'
                    : (first?.align ?? 'center') === 'right'
                      ? 'format_align_right'
                      : 'format_align_center'
                }
                size={18}
              />
            </button>
          )}
          <div className="bd-ctx-wrap">
            <button type="button" className="bd-ctx-btn" title="Text colour" aria-label="Text colour" onClick={() => setMenu(menu === 'color' ? null : 'color')}>
              <span className="bd-letter">
                A<i style={{ background: first?.color ?? '#1e1e1e' }} />
              </span>
            </button>
            {menu === 'color' && (
              <Popover className="bd-pop-ctx" onClose={() => setMenu(null)}>
                <Swatches colours={[...INK_COLOURS, { name: 'white', hex: '#ffffff' }]} value={first?.color} onPick={(hex) => setAll({ color: hex })} />
              </Popover>
            )}
          </div>
        </div>
      )}
      <div className="bd-ctx-group">
        <button type="button" className="bd-ctx-btn" title="Duplicate (⌘D)" aria-label="Duplicate" onClick={onDuplicate}>
          <Icon name="content_copy" size={18} />
        </button>
        <button type="button" className="bd-ctx-btn" title="Bring to front" aria-label="Bring to front" onClick={onFront}>
          <Icon name="flip_to_front" size={18} />
        </button>
        <button type="button" className="bd-ctx-btn" title="Send to back" aria-label="Send to back" onClick={onBack}>
          <Icon name="flip_to_back" size={18} />
        </button>
        <button type="button" className="bd-ctx-btn bd-danger" title="Delete (⌫)" aria-label="Delete" onClick={onDelete}>
          <Icon name="delete" size={18} />
        </button>
      </div>
    </div>
  )
}
